import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxChip, OnyxPaginated, OnyxTableNotFound } from '@onyx/angular';
import {
  ActionHelper,
  OnyxAvatarComponent,
  OnyxBadgeComponent,
  OnyxButtonComponent,
  OnyxChipsComponent,
  OnyxDatePipe,
  OnyxDropdownDirective,
  OnyxIconButtonComponent,
  OnyxInformationHeadingComponent,
  OnyxInformationListComponent,
  OnyxInformationRowComponent,
  OnyxModalComponent,
  OnyxNamePipe,
  OnyxPhonePipe,
  OnyxTableComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { isString } from 'lodash';
import {
  catchError,
  EMPTY,
  skip,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { AuthStore } from '../../../../../auth/common/stores/auth.store';
import { UnavailabilitiesComponent } from '../../../../../common/components/unavailabilities/unavailabilities.component';
import { UnavailabilityIconComponent } from '../../../../../common/components/unavailabilities/unavailability-icon/unavailability-icon.component';
import { SHORT_PAGINATION } from '../../../../../common/constants/common/short-pagination';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { StatisticsType } from '../../../../../common/enums/statistics-type';
import type { EmployeeStatistics } from '../../../../../common/interfaces/statistics/employee-statistics';
import { StatisticsService } from '../../../../../common/services/statistics.service';
import { ModalsStore } from '../../../../../common/stores/modals.store';
import { FleetHelper } from '../../../../fleet/common/helpers/fleet.helper';
import type { SimplifiedFleet } from '../../../../fleet/common/interfaces/fleet';
import { EmployeeStatusComponent } from '../../common/components/employee-status/employee-status.component';
import { EMPLOYEES_ASSIGNED_VEHICLES_LIST_COLUMNS } from '../../common/constants/employees-assigned-vehicles-list-columns';
import { EmployeeModalSection } from '../../common/enums/employee-modal-section';
import { EmployeeStatus } from '../../common/enums/employee-status';
import {
  EMPLOYEE_HEADER_ACTIONS,
  EmployeeHelper,
} from '../../common/helpers/employee.helper';
import type {
  Employee,
  SimplifiedEmployee,
} from '../../common/interfaces/employee';
import { EmployeesService } from '../../common/services/employees.service';

export type EmployeeModalData = Employee | SimplifiedEmployee | string;

@Component({
  selector: 'app-employee-modal',
  imports: [
    OnyxModalComponent,
    OnyxIconButtonComponent,
    OnyxIconButtonComponent,
    OnyxInformationRowComponent,
    EmployeeStatusComponent,
    OnyxDatePipe,
    OnyxTableComponent,
    OnyxBadgeComponent,
    OnyxChipsComponent,
    OnyxAvatarComponent,
    OnyxButtonComponent,
    TranslatePipe,
    OnyxPhonePipe,
    forwardRef(() => UnavailabilitiesComponent),
    OnyxDropdownDirective,
    OnyxTooltipDirective,
    UnavailabilityIconComponent,
    OnyxNamePipe,
    OnyxInformationHeadingComponent,
    OnyxInformationListComponent,
  ],
  templateUrl: './employee-modal.component.html',
  styleUrl: './employee-modal.component.scss',
})
export class EmployeeModalComponent {
  protected readonly employeeData = inject<EmployeeModalData>(DIALOG_DATA);
  protected readonly authStore = inject(AuthStore);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly employeesService = inject(EmployeesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly statisticsService = inject(StatisticsService);
  private readonly fleetHelper = inject(FleetHelper);
  private readonly employeeHelper = inject(EmployeeHelper);
  private readonly modalsStore = inject(ModalsStore);

  protected readonly I18N = 'employees.employeeModal';
  protected readonly EMPLOYEES_ASSIGNED_VEHICLES_LIST_COLUMNS =
    EMPLOYEES_ASSIGNED_VEHICLES_LIST_COLUMNS;
  protected readonly ASSIGNED_VEHICLES_NOT_FOUND: OnyxTableNotFound = {
    icon: { name: 'menu-fleet-filled', size: 32 },
    title: `${this.I18N}.assignedVehicles.empty.title`,
    text: `${this.I18N}.assignedVehicles.empty.text`,
  };

  protected readonly ActionHelper = ActionHelper;
  protected readonly EmployeeStatus = EmployeeStatus;
  protected readonly DictionaryCode = DictionaryCode;
  protected readonly EmployeeModalSection = EmployeeModalSection;

  private readonly uuid: string;

  protected employee = signal<Employee | null>(null);
  protected assignedVehicles = signal<OnyxPaginated<SimplifiedFleet> | null>(
    null,
  );
  protected section = this.modalsStore.getActiveSection(
    EmployeeModalComponent.name,
    () => this.sections(),
  );
  protected loading = signal(false);
  protected error = signal(false);
  protected sectionLoading = signal(false);
  protected pagination = signal(SHORT_PAGINATION);
  protected employee$ = new Subject<void>();
  protected close$ = new Subject<void>();

  protected options = computed(() =>
    this.employee()
      ? this.employeeHelper.getOptions(
          this.employee()!,
          { close$: this.close$ },
          EMPLOYEE_HEADER_ACTIONS,
        )
      : null,
  );
  protected hasHierarchicalEdit = computed(() => {
    const employee = this.employee();
    if (!employee) return false;

    return (
      !EmployeeHelper.isArchived(employee) &&
      this.employeeHelper.hasHierarchicalEditPermission(employee)
    );
  });
  protected sections = signal<OnyxChip<EmployeeModalSection>[]>([
    ...(this.fleetHelper.hasModule()
      ? [
          {
            name: 'labels.assignedVehicles',
            value: EmployeeModalSection.ASSIGNED_VEHICLES,
          },
        ]
      : []),
    {
      name: 'unavailabilities.unavailabilities',
      value: EmployeeModalSection.PLANNED_UNAVAILABILITIES,
    },
  ]);

  private loadAssignedVehicles$ = new Subject<void>();

  constructor() {
    this.uuid = isString(this.employeeData)
      ? this.employeeData
      : this.employeeData.uuid;

    this.employee$
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(() =>
          this.employeesService.getEmployee(this.uuid).pipe(
            catchError(() => {
              this.employee.set(null);
              this.loading.set(false);
              this.error.set(true);

              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((employee) => {
        this.employee.set(employee);
        this.loading.set(false);
        this.error.set(false);
      });

    if (
      isString(this.employeeData) ||
      !EmployeeHelper.isEmployeeType(this.employeeData)
    ) {
      this.employee$.next();
    } else {
      this.employee.set(this.employeeData);
    }

    this.loadAssignedVehicles$
      .pipe(
        skip(1),
        tap(() => this.sectionLoading.set(true)),
        switchMap(() =>
          this.employeesService.getAssignedVehicles(
            this.uuid,
            this.pagination(),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((assignedVehicles) => {
        this.assignedVehicles.set(assignedVehicles);
        this.sectionLoading.set(false);
      });

    effect(() => {
      this.pagination();
      untracked(() => this.loadAssignedVehicles$.next());
    });

    const getStatistics$ = () =>
      this.uuid
        ? this.statisticsService.getStatistics(StatisticsType.EMPLOYEE, {
            employeeUuid: this.uuid,
          })
        : EMPTY;

    this.employeesService.reload$
      .pipe(
        tap(() => {
          this.employee$.next();
          this.loadAssignedVehicles$.next();
        }),
        startWith(undefined),
        switchMap(() => getStatistics$()),
      )
      .subscribe((statistics) => this.updateSections(statistics));
  }

  protected edit(): void {
    const employee = this.employee();
    if (employee) this.employeeHelper.edit(employee);
  }

  protected openFleetModal(fleet: SimplifiedFleet): void {
    if (fleet) this.fleetHelper.openModal(fleet);
  }

  protected assignVehicles(): void {
    const employee = this.employee();
    if (!employee) return;

    this.employeeHelper.changeVehicles(employee);
  }

  private updateSections(statistics: EmployeeStatistics): void {
    this.sections.update((section) =>
      section.map((section) => ({
        ...section,
        count: statistics[section.value],
      })),
    );
  }
}
